import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../ui/dialog";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { T, PRODUCTS, addDays } from "./clientsTokens";
import { StripeCustomerPicker } from "./StripeCustomerPicker";
import { CURRENCIES } from "../../../lib/money";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CS = `${API}/customer-success`;

const Field = ({ label, hint, children }) => (
  <div className="min-w-0">
    <label className="text-[11.5px] font-semibold" style={{ color: T.ink2 }}>{label}</label>
    {children}
    {hint && <div className="text-[11px] mt-0.5" style={{ color: T.ink3 }}>{hint}</div>}
  </div>
);

const primary = "bg-[#FFD108] hover:bg-[#E0B800] text-[#121212] font-semibold disabled:bg-[#E6E2D9] disabled:text-[#B5AFA3]";

const contactName = (c) => `${c.first_name || ""} ${c.last_name || ""}`.trim();

/* Add / Edit client — the only place `Client since` is entered (§28.5, user choice). */
export const ClientFormDialog = ({ open, onOpenChange, client, onSaved }) => {
  const editing = !!client;
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const [orgs, setOrgs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [confirmRename, setConfirmRename] = useState(false);

  useEffect(() => {
    if (!open) return;
    setConfirmRename(false);
    axios.get(`${API}/lookups/industry_type`)
      .then((r) => setIndustries((r.data?.items || []).map((i) => i.label))).catch(() => setIndustries([]));
    axios.get(`${CS}/jira-organisations`)
      .then((r) => setOrgs(r.data?.organisations || [])).catch(() => setOrgs([]));
    if (client?.client_id) {
      axios.get(`${CS}/clients/${encodeURIComponent(client.client_id)}/contacts`)
        .then((r) => setContacts(r.data?.contacts || [])).catch(() => setContacts([]));
    } else setContacts([]);
  }, [open, client]);

  useEffect(() => {
    if (!open) return;
    setF({
      client_id: client?.client_id || "", company_name: client?.company_name || "",
      legal_name: client?.legal_name || "",
      jira_org_name: client?.jira_org_name || "", contact_name: client?.contact_name || "",
      email: client?.email || "", client_since: client?.client_since || "",
      country: client?.country || "Australia", currency: (client?.currency || "AUD").toUpperCase(),
      industry_type: client?.industry_type || "",
      contracted_mrr: client?.contracted_mrr ?? "",
      is_vip: !!client?.is_vip, has_sla: !!client?.has_sla,
      products: client?.products || [],
    });
  }, [open, client]);

  const oldId = (client?.client_id || "").trim().toUpperCase();
  const newId = (f.client_id || "").trim().toUpperCase();
  const idChanged = editing && !!newId && newId !== oldId;

  const save = async () => {
    const cid = newId;
    if (!cid) { toast.error("A client ID is required"); return; }
    if (!(f.company_name || "").trim()) { toast.error("A company name is required"); return; }
    if (idChanged && !confirmRename) { setConfirmRename(true); return; }
    setBusy(true);
    try {
      if (idChanged) {
        const r = await axios.post(`${CS}/clients/${encodeURIComponent(oldId)}/change-id`,
          { new_client_id: cid });
        const n = Object.values(r.data?.moved || {}).reduce((a, b) => a + b, 0);
        toast.success(`Client ID is now ${cid}${n ? ` — ${n} linked record${n === 1 ? "" : "s"} moved with it` : ""}`);
      }
      await axios.put(`${CS}/clients/${encodeURIComponent(cid)}`, {
        company_name: f.company_name.trim(),
        legal_name: f.legal_name || null,
        jira_org_name: f.jira_org_name || null,
        contact_name: f.contact_name || null,
        email: f.email || null,
        client_since: f.client_since || null,
        country: f.country || "Australia",
        currency: f.currency || "AUD",
        industry_type: f.industry_type || null,
        contracted_mrr: f.contracted_mrr === "" || f.contracted_mrr === null
          ? null : Number(f.contracted_mrr),
        is_vip: !!f.is_vip, has_sla: !!f.has_sla, products: f.products,
      });
      if (!idChanged) toast.success(editing ? "Client updated" : "Client added");
      setConfirmRename(false);
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save the client"); }
    finally { setBusy(false); }
  };

  const toggleProduct = (k) => setF((p) => ({
    ...p, products: p.products.includes(k) ? p.products.filter((x) => x !== k) : [...p.products, k],
  }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl bg-white" data-testid="client-form-dialog">
        <DialogHeader><DialogTitle>{editing ? "Edit client" : "Add a client"}</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Client ID *" hint={editing ? "Renaming it moves every linked record with it." : "e.g. BORGER"}>
            <Input value={f.client_id || ""} data-testid="client-form-id"
                   onChange={(e) => { setConfirmRename(false); setF({ ...f, client_id: e.target.value.toUpperCase() }); }}
                   className="mt-1 h-9 border-[#DAD5CA]" />
          </Field>
          <Field label="Company name *">
            <Input value={f.company_name || ""} data-testid="client-form-name"
                   onChange={(e) => setF({ ...f, company_name: e.target.value })}
                   className="mt-1 h-9 border-[#DAD5CA]" />
          </Field>
          <Field label="Legal name" hint="The registered entity. Titles the client record.">
            <Input value={f.legal_name || ""} data-testid="client-form-legal-name"
                   onChange={(e) => setF({ ...f, legal_name: e.target.value })}
                   className="mt-1 h-9 border-[#DAD5CA]" />
          </Field>
          <Field label="Jira organisation" hint="The organisations on our Jira tickets.">
            <select value={f.jira_org_name || ""} data-testid="client-form-jira"
                    onChange={(e) => setF({ ...f, jira_org_name: e.target.value })}
                    className="mt-1 h-9 w-full rounded-md border border-[#DAD5CA] bg-white px-2 text-[13px]">
              <option value="">Not linked</option>
              {f.jira_org_name && !orgs.some((o) => o.name === f.jira_org_name) && (
                <option value={f.jira_org_name}>{f.jira_org_name}</option>
              )}
              {orgs.map((o) => <option key={o.name} value={o.name}>{o.name}</option>)}
            </select>
          </Field>
          <Field label="Client since" hint="When they became a client.">
            <Input type="date" value={f.client_since || ""} data-testid="client-form-since"
                   onChange={(e) => setF({ ...f, client_since: e.target.value })}
                   className="mt-1 h-9 border-[#DAD5CA]" />
          </Field>
          <Field label="Main contact"
            hint={editing && contacts.length === 0 ? "No saved contacts — manage contacts on the client record." : undefined}>
            {editing ? (
              <select value={f.contact_name || ""} data-testid="client-form-contact"
                      disabled={contacts.length === 0}
                      onChange={(e) => {
                        const name = e.target.value;
                        const c = contacts.find((x) => contactName(x) === name);
                        setF({ ...f, contact_name: name, email: c ? (c.email || "") : "" });
                      }}
                      className="mt-1 h-9 w-full rounded-md border border-[#DAD5CA] bg-white px-2 text-[13px] disabled:bg-[#F6F4EF]">
                <option value="">Nobody named</option>
                {f.contact_name && !contacts.some((c) => contactName(c) === f.contact_name) && (
                  <option value={f.contact_name}>{f.contact_name}</option>
                )}
                {contacts.map((c) => (
                  <option key={c.id} value={contactName(c)}>
                    {contactName(c)}{c.role ? ` · ${c.role}` : ""}
                  </option>
                ))}
              </select>
            ) : (
              <Input value={f.contact_name || ""} data-testid="client-form-contact"
                     onChange={(e) => setF({ ...f, contact_name: e.target.value })}
                     className="mt-1 h-9 border-[#DAD5CA]" />
            )}
          </Field>
          <Field label="Contact email" hint={editing ? "Comes from the main contact." : undefined}>
            <Input value={f.email || ""} data-testid="client-form-email" readOnly={editing}
                   onChange={(e) => setF({ ...f, email: e.target.value })}
                   className={`mt-1 h-9 border-[#DAD5CA] ${editing ? "bg-[#F6F4EF]" : ""}`} />
          </Field>
          <Field label="Industry type" hint="Also answers the Health Check's 'what best describes your business'.">
            <select value={f.industry_type || ""} data-testid="client-form-industry"
                    onChange={(e) => setF({ ...f, industry_type: e.target.value })}
                    className="mt-1 h-9 w-full rounded-md border border-[#DAD5CA] bg-white px-2 text-[13px]">
              <option value="">Not set</option>
              {f.industry_type && !industries.includes(f.industry_type) && (
                <option value={f.industry_type}>{f.industry_type}</option>
              )}
              {industries.map((i) => <option key={i} value={i}>{i}</option>)}
            </select>
          </Field>
          <Field label="Country" hint="Where they trade from.">
            <Input value={f.country || ""} data-testid="client-form-country"
                   onChange={(e) => setF({ ...f, country: e.target.value })}
                   className="mt-1 h-9 border-[#DAD5CA]" />
          </Field>
          <Field label="Billing currency" hint={(f.currency || "AUD") === "AUD" ? "Quotes carry 10% GST." : "Quotes are tax-free exports — no tax collected."}>
            <select value={f.currency || "AUD"} data-testid="client-form-currency"
                    onChange={(e) => setF({ ...f, currency: e.target.value })}
                    className="mt-1 h-9 w-full rounded-md border border-[#DAD5CA] bg-white px-2 text-[13px]">
              {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
            </select>
          </Field>
        </div>
        {editing && (
          <div className="pt-1">
            <Field label="Contracted MRR (invoiced outside Stripe)"
                   hint={client?.contracted_mrr_set_by
                     ? `Monthly ex-GST for the legacy Allocate/Operator book. Currently set by ${client.contracted_mrr_set_by === "seed_contracted_mrr" ? "the one-off seed from their accepted quotes" : client.contracted_mrr_set_by} — correct it if it's wrong. Ignored while they have a live Stripe subscription, and not counted at all once the client is inactive.`
                     : "Monthly ex-GST for the legacy Allocate/Operator book. Leave blank if they don't pay us monthly. Ignored while they have a live Stripe subscription — Stripe wins. Not counted at all once the client is marked inactive."}>
              <div className="mt-1 flex items-center gap-1.5">
                <span className="text-[13px]" style={{ color: "#8A857A" }}>
                  {f.currency === "GBP" ? "£" : f.currency === "USD" ? "US$" : "A$"}
                </span>
                <input type="number" min="0" step="0.01" value={f.contracted_mrr}
                  data-testid="client-form-contracted-mrr"
                  onChange={(e) => setF({ ...f, contracted_mrr: e.target.value })}
                  placeholder="0.00"
                  className="h-9 w-40 rounded-md border border-[#DAD5CA] bg-white px-2 text-[13px]" />
                <span className="text-[12px]" style={{ color: "#8A857A" }}>per month</span>
              </div>
            </Field>
          </div>
        )}
        {editing && (
          <div className="pt-1">
            <Field label="Stripe customer"
                   hint="Linking applies straight away — their subscriptions and invoices move with them.">
              <StripeCustomerPicker clientId={client?.client_id} clientName={client?.company_name} />
            </Field>
          </div>
        )}
        <div className="flex items-center gap-4 pt-1">
          {[["is_vip", "VIP"], ["has_sla", "Has an SLA"]].map(([k, label]) => (
            <label key={k} className="flex items-center gap-2 text-[12.5px]" style={{ color: T.ink }}>
              <input type="checkbox" checked={!!f[k]} onChange={(e) => setF({ ...f, [k]: e.target.checked })}
                     data-testid={`client-form-${k}`} className="w-3.5 h-3.5 accent-[#1A1A1A]" />
              {label}
            </label>
          ))}
        </div>
        <div>
          <div className="text-[11.5px] font-semibold mb-1.5" style={{ color: T.ink2 }}>Products</div>
          <div className="flex flex-wrap gap-1.5">
            {PRODUCTS.map((p) => {
              const on = (f.products || []).includes(p.key);
              return (
                <button key={p.key} onClick={() => toggleProduct(p.key)} title={p.name}
                        data-testid={`client-form-product-${p.key}`}
                        className="mono h-7 px-2.5 rounded-full text-[11px]"
                        style={on ? { background: T.accentTint, border: `1px solid ${T.accentEdge}`, fontWeight: 700, color: T.ink }
                                  : { background: "#fff", border: `1px solid ${T.border}`, color: T.ink2 }}>{p.key}</button>
              );
            })}
          </div>
        </div>
        {confirmRename && (
          <div className="rounded-[9px] px-3.5 py-3 text-[12.5px]" data-testid="client-form-rename-confirm"
            style={{ background: "#FFF7D1", border: "1px solid #E8D48A", color: "#6B5600" }}>
            <b>Changing {oldId} to {newId}.</b> The ID is the key every record points at, so these
            move with it: to-dos, quotes, activity and touches, meetings, contacts, the journey,
            the runbook, health checks (with invitations and backlog items) and imported HubSpot
            history. Anything already sent out carrying the old ID — a Health Check link, for
            instance — stops working.
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" className="border-[#DAD5CA]"
            onClick={() => (confirmRename ? setConfirmRename(false) : onOpenChange(false))}
            data-testid="client-form-cancel">
            {confirmRename ? "Keep editing" : "Cancel"}
          </Button>
          <Button onClick={save} disabled={busy} className={primary} data-testid="client-form-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" />
              : confirmRename ? `Change the ID and save`
                : editing ? "Save changes" : "Add client"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* Log a touch — works for one client or a whole selection. */
export const TouchDialog = ({ open, onOpenChange, clients, onSaved, today }) => {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const many = (clients || []).length > 1;

  useEffect(() => {
    if (!open) return;
    setF({ type: "call", activity_date: today, rep: "", outcome: "", notes: "" });
  }, [open, today]);

  const save = async () => {
    setBusy(true);
    try {
      for (const c of clients) {
        await axios.post(`${CS}/clients/${encodeURIComponent(c.client_id)}/touches`, {
          type: f.type, activity_date: f.activity_date, rep: f.rep || null,
          outcome: f.outcome || null, notes: f.notes || "",
        });
      }
      toast.success(many ? `Touch logged against ${clients.length} clients` : "Touch logged");
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not log the touch"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-white" data-testid="touch-dialog">
        <DialogHeader>
          <DialogTitle>{many ? `Log a touch against ${clients.length} clients` : `Log a touch — ${clients?.[0]?.company_name || ""}`}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Type">
            <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
              <SelectTrigger className="mt-1 h-9 border-[#DAD5CA]" data-testid="touch-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["call", "email", "meeting", "site_visit", "other"].map((s) => (
                  <SelectItem key={s} value={s} className="capitalize">{s.replace("_", " ")}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Date">
            <Input type="date" value={f.activity_date || ""} data-testid="touch-date"
                   onChange={(e) => setF({ ...f, activity_date: e.target.value })}
                   className="mt-1 h-9 border-[#DAD5CA]" />
          </Field>
          <Field label="Who made contact">
            <Input value={f.rep || ""} onChange={(e) => setF({ ...f, rep: e.target.value })}
                   className="mt-1 h-9 border-[#DAD5CA]" />
          </Field>
          <Field label="Outcome">
            <Input value={f.outcome || ""} onChange={(e) => setF({ ...f, outcome: e.target.value })}
                   className="mt-1 h-9 border-[#DAD5CA]" />
          </Field>
        </div>
        <Field label="Notes">
          <Textarea rows={3} value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })}
                    className="mt-1 border-[#DAD5CA]" data-testid="touch-notes" />
        </Field>
        <DialogFooter>
          <Button variant="outline" className="border-[#DAD5CA]" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={busy} className={primary} data-testid="touch-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Log the touch"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* Import meeting notes — match to a client before anything is saved. */
export const ImportNotesDialog = ({ open, onOpenChange, clients, onSaved, today }) => {
  const [cid, setCid] = useState("");
  const [f, setF] = useState({ raw_notes: "", meeting_date: "", sentiment: "none", health_score: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setCid(""); setF({ raw_notes: "", meeting_date: today, sentiment: "none", health_score: "" }); } }, [open, today]);

  const save = async () => {
    if (!cid) { toast.error("Match the notes to a client first"); return; }
    if (!f.raw_notes.trim()) { toast.error("Paste the meeting notes first"); return; }
    setBusy(true);
    try {
      const body = { raw_notes: f.raw_notes, meeting_date: f.meeting_date || null };
      if (f.sentiment !== "none") {
        body.sentiment = f.sentiment;
        if (f.health_score) body.health_score = parseInt(f.health_score, 10);
      }
      const r = await axios.post(`${CS}/clients/${encodeURIComponent(cid)}/meetings`, body);
      toast.success(r.data.health_check ? "Notes saved · health check logged" : "Meeting notes saved");
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save the notes"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white" data-testid="import-notes-dialog">
        <DialogHeader><DialogTitle>Import meeting notes</DialogTitle></DialogHeader>
        <p className="text-[13px]" style={{ color: T.ink2 }}>Match the notes to a client before anything is saved.</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Client *">
            <Select value={cid} onValueChange={setCid}>
              <SelectTrigger className="mt-1 h-9 border-[#DAD5CA]" data-testid="import-client">
                <SelectValue placeholder="Pick the client" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {(clients || []).map((c) => (
                  <SelectItem key={c.client_id} value={c.client_id}>{c.company_name} · {c.client_id}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Meeting date">
            <Input type="date" value={f.meeting_date} onChange={(e) => setF({ ...f, meeting_date: e.target.value })}
                   className="mt-1 h-9 border-[#DAD5CA]" data-testid="import-date" />
          </Field>
          <Field label="Sentiment" hint="Adding one records a health check too.">
            <Select value={f.sentiment} onValueChange={(v) => setF({ ...f, sentiment: v })}>
              <SelectTrigger className="mt-1 h-9 border-[#DAD5CA]" data-testid="import-sentiment"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Don't record a check</SelectItem>
                {["positive", "neutral", "negative"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          {f.sentiment !== "none" && (
            <Field label="Score out of 10">
              <Input type="number" min="1" max="10" value={f.health_score} data-testid="import-score"
                     onChange={(e) => setF({ ...f, health_score: e.target.value })}
                     className="mt-1 h-9 border-[#DAD5CA]" />
            </Field>
          )}
        </div>
        <Field label="Notes *">
          <Textarea rows={8} value={f.raw_notes} onChange={(e) => setF({ ...f, raw_notes: e.target.value })}
                    placeholder="Paste the meeting notes here" data-testid="import-notes"
                    className="mt-1 border-[#DAD5CA]" />
        </Field>
        <DialogFooter>
          <Button variant="outline" className="border-[#DAD5CA]" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={busy} className={primary} data-testid="import-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save the notes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
